/**
 * Application configuration and environment variables
 * All secrets and configuration should be handled here
 */

export const config = {
  // Firebase (POS staff + public POS callables)
  firebase: {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || '',
  },

  /**
   * Customer Auth (kingspark-app) — same project as Android.
   * Used for web /order login; never for staff POS Auth.
   */
  customerAppFirebase: {
    apiKey: import.meta.env.VITE_CUSTOMER_APP_FIREBASE_API_KEY || '',
    authDomain: import.meta.env.VITE_CUSTOMER_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: import.meta.env.VITE_CUSTOMER_APP_FIREBASE_PROJECT_ID || '',
    storageBucket: import.meta.env.VITE_CUSTOMER_APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: import.meta.env.VITE_CUSTOMER_APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: import.meta.env.VITE_CUSTOMER_APP_FIREBASE_APP_ID || '',
  },

  // App Environment
  env: (import.meta.env.VITE_APP_ENV || 'development') as 'development' | 'staging' | 'production',
  isDevelopment: import.meta.env.VITE_APP_ENV === 'development' || !import.meta.env.VITE_APP_ENV,
  isProduction: import.meta.env.VITE_APP_ENV === 'production',

  // Feature Flags
  useMockData: import.meta.env.VITE_USE_MOCK_DATA === 'true',

  // API Configuration
  apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
  /** Kings Park VPS reports/audit API (empty = Firestore only). Set at build time for Hostinger dev/prod. */
  apiBaseUrl: (import.meta.env.VITE_API_BASE_URL || '').trim(),
  
  // SmartConnect API Configuration
  smartconnect: {
    apiHost: import.meta.env.VITE_SMARTCONNECT_API_HOST || ((import.meta.env.VITE_APP_ENV === 'development' || !import.meta.env.VITE_APP_ENV)
      ? 'https://api-dev.smart-connect.cloud/POS' 
      : 'https://api.smart-connect.cloud/POS'),
  },

  // Email Configuration (Resend)
  email: {
    enabled: !!import.meta.env.VITE_RESEND_API_KEY,
    fromEmail: import.meta.env.VITE_RESEND_FROM_EMAIL || 'noreply@kingspark.co.nz',
  },

  // App Metadata
  appName: 'Kings Park POS',
  appVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0',

  // Uber Eats – official docs: https://developer.uber.com/docs/eats/guides/authentication
  // Two flows: authorization_code (store activation only, scope eats.pos_provisioning) and client_credentials (Store/Menu/Order/Reporting)
  uber: {
    useSandbox: import.meta.env.VITE_UBER_USE_SANDBOX !== 'false',
    clientId: import.meta.env.VITE_UBER_CLIENT_ID || '',
    clientSecret: import.meta.env.VITE_UBER_CLIENT_SECRET || '',
    redirectUri: import.meta.env.VITE_UBER_REDIRECT_URI || (typeof window !== 'undefined' ? `${window.location.origin}/` : ''),
    // Authorization code flow: only eats.pos_provisioning (store discovery & integration activation)
    authCodeScopes: (import.meta.env.VITE_UBER_AUTH_SCOPES || 'eats.pos_provisioning').trim().split(/\s+/).filter(Boolean),
    // Client credentials flow: for Store, Menu, Order, Reporting (eats.store eats.order; cache token, 100 req/hour limit)
    clientCredentialsScopes: (import.meta.env.VITE_UBER_CLIENT_SCOPES || 'eats.store eats.order').trim().split(/\s+/).filter(Boolean),
  },
};

/**
 * Validate that all required environment variables are set
 */
export const validateConfig = (): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  const firebaseChecks: [keyof typeof config.firebase, string][] = [
    ['apiKey', 'VITE_FIREBASE_API_KEY'],
    ['authDomain', 'VITE_FIREBASE_AUTH_DOMAIN'],
    ['projectId', 'VITE_FIREBASE_PROJECT_ID'],
    ['storageBucket', 'VITE_FIREBASE_STORAGE_BUCKET'],
    ['messagingSenderId', 'VITE_FIREBASE_MESSAGING_SENDER_ID'],
    ['appId', 'VITE_FIREBASE_APP_ID'],
  ];
  for (const [key, envName] of firebaseChecks) {
    const v = config.firebase[key];
    if (typeof v === 'string' && !v.trim()) {
      errors.push(`${envName} is not set`);
    }
  }

  if (config.useMockData && config.isProduction) {
    errors.push('Cannot use mock data in production');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Get data source - now always returns 'firebase' or 'mock'
 */
export const getDataSource = (): 'mock' | 'firebase' => {
  if (config.useMockData) {
    console.warn('⚠️  Using mock data. This should only be used in development.');
    return 'mock';
  }
  return 'firebase';
};

/**
 * Log configuration on app start (without sensitive data)
 */
export const logConfig = () => {
  const { valid, errors } = validateConfig();

  if (config.isDevelopment) {
    const dataSource = getDataSource();
    console.info('🔧 App Configuration:', {
      env: config.env,
      dataSource,
      firebaseConfigured: firebaseCoreConfigured(),
      version: config.appVersion,
    });
  } else {
    console.info(`Kings Park POS ${config.appVersion} (${config.env})`);
  }

  if (!valid) {
    console.error('❌ Configuration errors:', errors);
  }
};

function firebaseCoreConfigured(): boolean {
  return Boolean(
    config.firebase.apiKey?.trim() &&
      config.firebase.projectId?.trim() &&
      config.firebase.authDomain?.trim() &&
      config.firebase.appId?.trim()
  );
}

/**
 * Security utilities
 */
export const security = {
  /**
   * Check if we're running in a secure context (HTTPS or localhost)
   */
  isSecureContext: (): boolean => {
    if (typeof window === 'undefined') return true;
    return window.isSecureContext || window.location.hostname === 'localhost';
  },

  /**
   * Check if we're in development mode
   */
  isDevelopment: (): boolean => {
    return config.isDevelopment;
  },
};

export default config;
